/* Drill-down direction — détail d'une session d'examen : copies + corrections
   (spec 10a, Écran B). Lecture seule : la direction ne corrige/publie/override
   jamais une copie prof. */

import { useEffect, useState } from 'react';
import { Eye } from '@phosphor-icons/react';
import { useNavigate, useParams } from '../lib/routing';
import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { api } from '../lib/api';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface Copy {
  classroom_student_id: string;
  exam_copy_id: string | null;
  first_name: string;
  last_name: string;
  status: string;
  asset_count: number;
}
interface Detail {
  id: string;
  status: string;
  scheduled_at: string | null;
  exam_title: string;
  classroom_name: string;
  copies: Copy[];
}
interface CorrItem {
  sectionIndex: number;
  sectionTitle: string;
  score: number;
  max: number;
  feedback: string;
  unread?: boolean;
  letter?: string;
  scaleId?: string;
  letterLabel?: string;
}
interface CorrResult {
  totalScore: number;
  totalMax: number;
  overallFeedback: string;
  items: CorrItem[];
  model: string;
}
interface CorrRow {
  result: CorrResult;
  teacher_override: CorrResult | null;
  published_to_student: boolean;
}
interface Image {
  url: string;
  content_type: string;
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Brouillon',
  collecting: 'Collecte',
  correcting: 'Correction',
  done: 'Terminé',
};
const COPY_LABEL: Record<string, string> = {
  pending: 'À collecter',
  collected: 'Collectée',
  corrected: 'Corrigée',
  published: 'Publiée',
};
const COPY_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = {
  pending: 'outline',
  collected: 'secondary',
  corrected: 'default',
  published: 'default',
};

export default function DirectionExamSessionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCopyId, setOpenCopyId] = useState<string | null>(null);
  const [corr, setCorr] = useState<CorrRow | null | undefined>(undefined);
  const [images, setImages] = useState<Image[]>([]);

  useEffect(() => {
    if (!id) return;
    api.direction.supervision.examSessionCopies
      .query({ examSessionId: id })
      .then((d) => setDetail(d as Detail))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  const toggleCorrection = async (examCopyId: string) => {
    if (openCopyId === examCopyId) {
      setOpenCopyId(null);
      return;
    }
    setOpenCopyId(examCopyId);
    setCorr(undefined);
    setImages([]);
    try {
      const [c, imgs] = await Promise.all([
        api.direction.supervision.examCopyCorrection.query({ examCopyId }),
        api.direction.supervision.examCopyAssets.query({ examCopyId }),
      ]);
      setCorr(c as CorrRow | null);
      setImages(imgs as Image[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setCorr(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh bg-background flex flex-col">
        <Header />
        <HeaderContent width="md" showBurger onBack={() => navigate(-1)} backLabel="Retour">
          <h1 className={HEADER_TITLE_CLASS}>Session d'examen</h1>
        </HeaderContent>
        <div className="max-w-[880px] mx-auto px-4 pt-4 pb-16 flex-1 w-full">
          <div className="flex flex-col gap-[10px]">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-[70px] rounded-card" />)}
          </div>
        </div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="min-h-dvh bg-background flex flex-col">
        <Header />
        <HeaderContent width="md" showBurger onBack={() => navigate(-1)} backLabel="Retour">
          <h1 className={HEADER_TITLE_CLASS}>Session d'examen</h1>
        </HeaderContent>
        <div className="max-w-[880px] mx-auto px-4 pt-4 pb-16 flex-1 w-full">
          {error && <Alert>{error}</Alert>}
          <p className="text-muted-foreground">Session introuvable.</p>
        </div>
      </div>
    );
  }

  const collected = detail.copies.filter((c) => c.status !== 'pending').length;

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <Header />
      <HeaderContent width="md" showBurger onBack={() => navigate(-1)} backLabel="Retour" eyebrow="Mode direction" eyebrowIcon={<Eye size={11} weight="bold" />}>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <h1 className={HEADER_TITLE_CLASS}>{detail.exam_title}</h1>
            <span className={HEADER_BADGE_CLASS}>{STATUS_LABEL[detail.status] ?? detail.status}</span>
          </div>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            {detail.classroom_name} · {collected}/{detail.copies.length} copie{detail.copies.length > 1 ? 's' : ''} collectée{collected > 1 ? 's' : ''}
          </p>
        </div>
      </HeaderContent>
      <div className="max-w-[880px] mx-auto px-4 pt-4 pb-16 flex-1 w-full">
        {error && <Alert>{error}</Alert>}

        <div className="flex flex-col gap-[10px]">
          {detail.copies.map((c) => (
            <div key={c.classroom_student_id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-[14px] bg-card border-[1.5px] border-border rounded-card">
              <div className="basis-full min-w-0 sm:basis-0 sm:flex-1">
                <div className="font-bold text-foreground truncate">{c.first_name} {c.last_name}</div>
                <div className="text-[13px] text-muted-foreground mt-0.5">
                  {c.asset_count > 0 ? `${c.asset_count} photo${c.asset_count > 1 ? 's' : ''}` : 'Aucune photo'}
                </div>
              </div>
              <Badge variant={COPY_VARIANT[c.status] ?? 'outline'}>{COPY_LABEL[c.status] ?? c.status}</Badge>
              {c.exam_copy_id && c.status !== 'pending' && (
                <Button variant="secondary" className="min-h-11 min-w-11" onClick={() => void toggleCorrection(c.exam_copy_id!)}>
                  {openCopyId === c.exam_copy_id ? 'Fermer' : 'Voir la correction'}
                </Button>
              )}
              {openCopyId === c.exam_copy_id && c.exam_copy_id && (
                <div className="basis-full mt-2.5 pt-2.5 border-t border-border">
                  {corr === undefined ? (
                    <p className="text-[13px] text-muted-foreground">Chargement…</p>
                  ) : corr === null ? (
                    <p className="text-[13px] text-muted-foreground">Pas encore de correction pour cette copie.</p>
                  ) : (
                    <ReadOnlyCorrection row={corr} images={images} />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReadOnlyCorrection({ row, images }: { row: CorrRow; images: Image[] }) {
  const effective = row.teacher_override ?? row.result;
  return (
    <div className="flex flex-col gap-[14px] sm:flex-row sm:items-start">
      {images.length > 0 && (
        <div className="sm:w-[38%] sm:flex-none flex flex-col gap-2">
          {images.map((img, i) => (
            <a key={`${img.url}-${i}`} href={img.url} target="_blank" rel="noreferrer">
              <img src={img.url} alt={`Copie — photo ${i + 1}`} className="w-full max-h-[440px] object-contain rounded-card border border-border bg-card" />
            </a>
          ))}
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col gap-[10px]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[13px] font-extrabold text-foreground tabular-nums">
            {effective.totalMax > 0 ? `Total : ${effective.totalScore} / ${effective.totalMax}` : 'Notation par lettres'}
          </div>
          <Badge variant={row.published_to_student ? 'default' : 'secondary'}>
            {row.published_to_student ? 'Publiée à l\'élève' : 'Non publiée'}
          </Badge>
        </div>
        {effective.items.map((it, i) => (
          <div key={i} className="border border-border rounded-card p-3 flex flex-col gap-1.5">
            <div className="font-semibold text-[13.5px] text-foreground">{it.sectionTitle || `Section ${it.sectionIndex + 1}`}</div>
            {it.unread ? (
              <Badge variant="secondary">Non lue</Badge>
            ) : (
              <div className="text-[13px] text-muted-foreground">{it.scaleId !== undefined ? `Lettre : ${it.letter ?? '—'}${it.letterLabel ? ` — ${it.letterLabel}` : ''}` : `Note : ${it.score} / ${it.max}`}</div>
            )}
            {it.feedback && <p className="m-0 text-[13px] text-foreground">{it.feedback}</p>}
          </div>
        ))}
        {effective.overallFeedback && (
          <div className="flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-muted-foreground">Appréciation générale</span>
            <p className="m-0 text-[13px] text-foreground">{effective.overallFeedback}</p>
          </div>
        )}
      </div>
    </div>
  );
}
